import { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import Sidebar from "./Sidebar";
import Topbar from "./TopBar";

import {
  MdMessage,
  MdCalendarMonth,
  MdPeople,
  MdTrendingUp,
} from "react-icons/md";

import { api } from "../../api/axios";

const DashboardLayout = () => {
  const [stats, setStats] = useState({
    messagesTotal: null,
    messagesUnread: null,
    bookingsTotal: null,
    usersTotal: null,
  });

  /* =========================
     LOAD DASHBOARD STATS
  ========================= */
  const loadStats = async () => {
    try {
      const [messagesRes, bookingsRes, usersRes] =
        await Promise.allSettled([
          api.get("/contact-messages"),
          api.get("/bookings/admin"), // sipas strukturës suaj
          api.get("/users"), // vetëm nëse ekziston
        ]);

      // CONTACT MESSAGES
      if (messagesRes.status === "fulfilled") {
        const messages = messagesRes.value.data || [];
        setStats((s) => ({
          ...s,
          messagesTotal: messages.length,
          messagesUnread: messages.filter(
            (m) => m.isRead === false
          ).length,
        }));
      }

      // BOOKINGS
      if (bookingsRes.status === "fulfilled") {
        setStats((s) => ({
          ...s,
          bookingsTotal:
            bookingsRes.value.data?.length ?? 0,
        }));
      }

      // USERS (optional)
      if (usersRes.status === "fulfilled") {
        setStats((s) => ({
          ...s,
          usersTotal:
            usersRes.value.data?.length ?? 0,
        }));
      }
    } catch (err) {
      console.error(
        "Failed to load dashboard stats",
        err
      );
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  return (
    <div className="min-h-screen flex bg-gray-100">
      <Sidebar />

      <div className="flex-1 flex flex-col">
        <Topbar />

        <main className="flex-1 p-6 overflow-y-auto space-y-6">
          {/* =========================
              DASHBOARD INTRO / STATS
          ========================= */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {/* CONTACT MESSAGES */}
            <div className="bg-white rounded-2xl shadow p-5 flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">
                  Contact Messages
                </p>
                <h3 className="text-2xl font-semibold text-[#28424d]">
                  {stats.messagesTotal ?? "—"}
                </h3>
                {stats.messagesUnread > 0 && (
                  <p className="text-xs text-red-600 mt-1">
                    {stats.messagesUnread} unread
                  </p>
                )}
              </div>
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                <MdMessage
                  size={22}
                  className="text-emerald-600"
                />
              </div>
            </div>

            {/* BOOKINGS */}
            <div className="bg-white rounded-2xl shadow p-5 flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">
                  Bookings
                </p>
                <h3 className="text-2xl font-semibold text-[#28424d]">
                  {stats.bookingsTotal ?? "—"}
                </h3>
              </div>
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <MdCalendarMonth
                  size={22}
                  className="text-blue-600"
                />
              </div>
            </div>

            {/* USERS */}
            <div className="bg-white rounded-2xl shadow p-5 flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">
                  Users
                </p>
                <h3 className="text-2xl font-semibold text-[#28424d]">
                  {stats.usersTotal ?? "—"}
                </h3>
              </div>
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                <MdPeople
                  size={22}
                  className="text-purple-600"
                />
              </div>
            </div>

            {/* ACTIVITY (placeholder) */}
            <div className="bg-white rounded-2xl shadow p-5 flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">
                  Activity
                </p>
                <h3 className="text-2xl font-semibold text-[#28424d]">
                  —
                </h3>
              </div>
              <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                <MdTrendingUp
                  size={22}
                  className="text-orange-600"
                />
              </div>
            </div>
          </div>

          {/* =========================
              PAGE CONTENT
          ========================= */}
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
